import { useId, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { UserRole } from '../api/schema';
import { NotificationBell } from '../screens/notifications';
import { useOnClickOutside, useOnEscape } from '../lib/hooks';
import { showsTenantNav } from './navigation';

import { ProfileMenu } from './ProfileMenu';
import { CalendarIcon, SearchIcon } from './icons';

/**
 * The presets offered by the global date-range control, with their length in
 * days (`null` = all time, the default). A preset writes `start`/`end` into the
 * current URL — the same params the Dashboard and Documents filters read.
 */
export const DATE_RANGES = [
  { label: 'All time', days: null },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
] as const;

/** UTC `YYYY-MM-DD`, `offset` days before today — the backend's day convention. */
function utcDay(offset = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
}

/** The label for a `start`/`end` pair: a preset's name, else the literal range. */
function rangeLabel(start: string | null, end: string | null): string {
  if (start === null) return end === null ? 'All time' : `Until ${end}`;
  const preset = DATE_RANGES.find(
    (r) => r.days !== null && start === utcDay(r.days - 1) && (end === null || end === utcDay()),
  );
  return preset?.label ?? `${start} – ${end ?? utcDay()}`;
}

export interface TopBarProps {
  /** Title of the screen currently rendered in the content area. */
  title: string;
  /**
   * The signed-in role. Drives the header: a tenant role gets the full bar
   * (tenant chip + date range + document search); a platform role — which has no
   * tenant — gets only the title, the bell and the profile menu.
   */
  role: UserRole | null;
  /** Active tenant, or `null` when none has been selected yet. */
  tenantName: string | null;
}

/** The 56px top bar from the Docsight design. */
export function TopBar({ title, role, tenantName }: TopBarProps): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = rangeLabel(searchParams.get('start'), searchParams.get('end'));
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeMenuId = useId();
  const searchInputId = useId();

  const rangeRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(rangeRef, rangeOpen, () => { setRangeOpen(false); });
  useOnEscape(rangeOpen, () => { setRangeOpen(false); });

  // A tenant role has a tenant, so it gets the tenant-scoped controls; a platform
  // role (system/partner admin) has none, so those controls are not rendered.
  const tenantScoped = showsTenantNav(role);

  const onSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const form = event.currentTarget;
    const value = new FormData(form).get('q');
    const query = typeof value === 'string' ? value.trim() : '';
    if (query === '') return;
    void navigate(`/documents?q=${encodeURIComponent(query)}`);
  };

  return (
    <header className="topbar">
      <h1 className="screen-title">{title}</h1>

      {tenantScoped ? (
        <div className="tenant-chip" data-testid="tenant-chip">
          {tenantName ?? 'No tenant selected'}
          <span aria-hidden="true" className="caret">
            ▾
          </span>
        </div>
      ) : null}

      <div className="topbar-actions">
        {tenantScoped ? (
          <>
            <div className="menu-anchor" ref={rangeRef}>
              <button
                type="button"
                className="range-button"
                aria-haspopup="menu"
                aria-expanded={rangeOpen}
                aria-controls={rangeMenuId}
                onClick={() => {
                  setRangeOpen((open) => !open);
                }}
              >
                <CalendarIcon />
                <span className="sr-only">Date range: </span>
                {range}
                <span aria-hidden="true" className="caret">
                  ▾
                </span>
              </button>
              {rangeOpen ? (
                <div className="menu" id={rangeMenuId} role="menu" aria-label="Date range">
                  {DATE_RANGES.map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      role="menuitemradio"
                      aria-checked={option.label === range}
                      className="menu-item"
                      onClick={() => {
                        const next = new URLSearchParams(searchParams);
                        if (option.days === null) {
                          next.delete('start');
                          next.delete('end');
                        } else {
                          next.set('start', utcDay(option.days - 1));
                          next.set('end', utcDay());
                        }
                        setSearchParams(next);
                        setRangeOpen(false);
                      }}
                    >
                      {option.label}
                      {option.label === range ? (
                        <span aria-hidden="true" className="menu-tick">
                          ✓
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <form className="search" role="search" onSubmit={onSearch}>
              <label className="sr-only" htmlFor={searchInputId}>
                Search documents
              </label>
              <SearchIcon />
              <input
                id={searchInputId}
                name="q"
                type="search"
                className="search-input"
                placeholder="Search documents…"
              />
              <button type="submit" className="sr-only">
                Search
              </button>
            </form>
          </>
        ) : null}

        <NotificationBell />

        <ProfileMenu />
      </div>
    </header>
  );
}
